import { useState } from "react";
import { Send, Phone, Loader2, Paperclip, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, openDocument } from "@/lib/api";
import { SAMPLE_PRESETS } from "@/lib/constants";
import { toast } from "sonner";

const WHATSAPP_BG = "#0b141a";

export const WhatsAppSimulator = ({ onResolved }) => {
  const [fromNumber, setFromNumber] = useState("+919820012345");
  const [lawyerName, setLawyerName] = useState("Adv. Priya Menon");
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);

  const pushMsg = (m) => setMessages((prev) => [...prev, m]);

  const sendMessage = async (text) => {
    const body = (text ?? message).trim();
    if (!body) return;
    pushMsg({ side: "out", text: body });
    setMessage("");
    setTyping(true);
    try {
      const { data } = await api.post("/whatsapp/simulate", {
        from_number: fromNumber,
        lawyer_name: lawyerName,
        message: body,
      });
      setTimeout(() => {
        setTyping(false);
        pushMsg({ side: "in", text: data.outbound_message, status: data.status, channel: data.delivery_channel, attachments: data.attachments || [], source: data.source });
        toast.success(data.status === "replied" ? "WhatsApp reply sent successfully" : "Replied: case not found");
        onResolved?.();
      }, 900);
    } catch (e) {
      setTyping(false);
      const detail = e?.response?.data?.detail || "Failed to process message";
      pushMsg({ side: "in", text: detail, status: "not_found" });
      toast.error(detail);
      onResolved?.();
    }
  };

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-white/10 bg-[#111723]">
      <div className="border-b border-white/10 p-4">
        <h3 className="font-display text-lg font-bold text-slate-50">WhatsApp Simulator</h3>
        <p className="mt-0.5 text-xs text-slate-400">
          Act as a lawyer sending a case number. Watch the full loop run live.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Input
            data-testid="sim-from-number"
            value={fromNumber}
            onChange={(e) => setFromNumber(e.target.value)}
            placeholder="Lawyer phone"
            className="h-9 border-white/10 bg-[#0B0F17] font-mono-data text-xs text-slate-200"
          />
          <Input
            data-testid="sim-lawyer-name"
            value={lawyerName}
            onChange={(e) => setLawyerName(e.target.value)}
            placeholder="Lawyer name"
            className="h-9 border-white/10 bg-[#0B0F17] text-xs text-slate-200"
          />
        </div>
      </div>

      {/* Chat window */}
      <div className="flex-1 space-y-3 overflow-y-auto p-4" style={{ background: WHATSAPP_BG, minHeight: 320 }} data-testid="sim-chat-window">
        {messages.length === 0 && (
          <div className="flex h-full items-center justify-center">
            <p className="text-center text-xs text-slate-500">
              Send a case number like <span className="font-mono-data text-emerald-400">CIV-2026-8890</span> to start.
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.side === "out" ? "justify-end" : "justify-start"} fade-up`}>
            <div
              className={`max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed ${
                m.side === "out"
                  ? "rounded-br-sm bg-[#005c4b] text-slate-50"
                  : "rounded-bl-sm bg-[#202c33] text-slate-100"
              }`}
            >
              <span className="whitespace-pre-wrap">{m.text}</span>
              {m.source === "external_registry" && (
                <span className="mt-2 block w-fit rounded bg-blue-500/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-blue-300">
                  via National eCourts Registry
                </span>
              )}
              {m.attachments?.length > 0 && (
                <div className="mt-2 space-y-1.5 border-t border-white/10 pt-2">
                  {m.attachments.map((a, j) => (
                    a.case_id && a.doc_id ? (
                    <button
                      key={j}
                      onClick={() => openDocument(a.case_id, a.doc_id)}
                      data-testid={`sim-attachment-${j}`}
                      className="flex w-full items-center gap-2 rounded-md bg-black/20 px-2 py-1.5 text-xs text-emerald-200 transition-colors duration-200 hover:bg-black/40"
                    >
                      <Paperclip className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{a.filename}</span>
                      <Download className="ml-auto h-3.5 w-3.5 shrink-0" />
                    </button>
                    ) : (
                    <div key={j} data-testid={`sim-attachment-${j}`} className="flex items-center gap-2 rounded-md bg-black/20 px-2 py-1.5 text-xs text-emerald-200/70">
                      <Paperclip className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{a.filename}</span>
                    </div>
                    )
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {typing && (
          <div className="flex justify-start">
            <div className="flex items-center gap-1 rounded-lg rounded-bl-sm bg-[#202c33] px-4 py-3">
              <span className="typing-dot h-1.5 w-1.5 rounded-full bg-slate-400" />
              <span className="typing-dot h-1.5 w-1.5 rounded-full bg-slate-400" />
              <span className="typing-dot h-1.5 w-1.5 rounded-full bg-slate-400" />
            </div>
          </div>
        )}
      </div>

      {/* Presets */}
      <div className="flex flex-wrap gap-2 border-t border-white/10 px-4 pt-3">
        {SAMPLE_PRESETS.map((p) => (
          <button
            key={p.label}
            data-testid={`sim-preset-${p.label}`}
            onClick={() => sendMessage(p.msg)}
            className="rounded-full border border-white/10 bg-[#0B0F17] px-2.5 py-1 font-mono-data text-[11px] text-slate-300 transition-colors duration-200 hover:border-emerald-500/50 hover:text-emerald-400"
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Input */}
      <div className="flex items-center gap-2 p-4">
        <Input
          data-testid="whatsapp-input-field"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          placeholder="Type a case number..."
          className="h-10 border-white/10 bg-[#0B0F17] text-sm text-slate-100"
        />
        <Button
          data-testid="whatsapp-send-button"
          onClick={() => sendMessage()}
          className="h-10 bg-emerald-600 px-4 text-white hover:bg-emerald-700"
        >
          {typing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};
